import { useEffect, useState } from "react";
import { Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ItemsText, ItemText, ShipmentText } from "constants/text";
import { convertToTitleCase } from "utils/string";
import OrderDetailsLineItemGrid from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/OrderDetailsLineItemGrid.tsx";
import OrderDetailsShipmentHeader from "./OrderDetailsShipmentHeader";
import { ORDER_STATUS_REASONS } from "constants/orderStatusHeaderStatusReasons";
import {
	BackorderAndShipmentTextCount,
	OrderDetailsShipmentAccordionWrapper,
	StyledAccordionDetails,
	StyledAccordionSummary,
	StyledOrderAndShippingIcon,
	StyledOrderAndShippingStatusMessage
} from "components/OrderDetailsLineItem/orderDetailsShipmentAccordion.styles";
import { models } from "types/api/viewModels";
import { Chip } from "components/Chip";

interface LineItemProps {
	order: models["OrderDetailViewModel"] | null | undefined;
	originalOrder: models["OrderDetailViewModel"] | null | undefined;
	searchValue?: string | null;
	shipmentCount?: number;
	billTo?: models["BillToViewModel"] | null;
}

export const OrderDetailsShipmentAccordion = ({
	order,
	originalOrder,
	searchValue,
	shipmentCount,
	billTo
}: LineItemProps) => {
	const [shippingMessage, setShippingMessage] = useState("");
	const lineItems = originalOrder?.configurations?.map((config) => config.lineItems).flat();
	const foundAccordionSearch = order?.configurations?.find((item) =>
		item?.lineItems?.map((item) => item?.sku?.toUpperCase().includes((searchValue ?? "").toUpperCase()))
	);

	useEffect(() => {
		ORDER_STATUS_REASONS.forEach(
			(reason) => reason.status === order?.status && setShippingMessage(reason.description)
		);
	}, [order]);

	if (!foundAccordionSearch) return <></>;

	return (
		<OrderDetailsShipmentAccordionWrapper
			defaultExpanded
			data-testid="shipment-accordion"
			key={order?.orderId}
		>
			<StyledAccordionSummary
				expandIcon={<ExpandMoreIcon />}
				aria-controls="panel1a-content"
				id="panel1a-header-backorder"
			>
				<StyledOrderAndShippingIcon>
					<img
						src="/assets/LocalShipping.svg"
						alt="Order Details Shipping Logo"
						data-testid="shipping-logo"
					/>
					<BackorderAndShipmentTextCount>
						<span>
							{ShipmentText} {shipmentCount}
						</span>
						<span>
							(
							{lineItems?.length === 1
								? `${lineItems?.length} ${ItemText}`
								: `${lineItems?.length} ${ItemsText}`}
							)
						</span>
					</BackorderAndShipmentTextCount>
				</StyledOrderAndShippingIcon>
				<StyledOrderAndShippingStatusMessage>
					<Chip
						visible={Boolean(order?.status)}
						label={convertToTitleCase(order?.status)}
					/>
					<span>{shippingMessage}</span>
				</StyledOrderAndShippingStatusMessage>
			</StyledAccordionSummary>
			<StyledAccordionDetails>
				<OrderDetailsShipmentHeader
					orderDetail={order}
					trackingNumber={order?.shippingDetails?.trackingNumber}
					truckNumber={order?.shippingDetails?.truckNumber}
				/>
				<Divider />
				<OrderDetailsLineItemGrid
					orderDetail={order}
					key={order?.orderId}
					variant="detail"
					billTo={billTo}
				/>
			</StyledAccordionDetails>
		</OrderDetailsShipmentAccordionWrapper>
	);
};

export default OrderDetailsShipmentAccordion;
